import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import GetCookie from "../../utils/GetCookie";
import ConfirmationModal from "../../utils/ConfirmationModal";
import GenbiHeadingMappingModal from "./GenbiHeadingMappingModal";

const ALLOWED_DOC_TYPES = ["doc", "docx", "word"];

const isAllowedDocType = (doc) => {
  const fileType = (doc.file_type || doc.document_type || "").toLowerCase();
  if (fileType) return ALLOWED_DOC_TYPES.includes(fileType);
  const name = doc.document_name || doc.name || "";
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  return ALLOWED_DOC_TYPES.includes(ext);
};

const countValidDocs = (thread) =>
  (thread.documents || []).filter(isAllowedDocType).length;

const useOutsideClick = (ref, callback, disabled = false) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (disabled) return;
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback, disabled]);
};

const THREADS_PAGE_SIZE = 25;
const DOCS_PAGE_SIZE = 25;
const BACKFILL_MAX_PAGES = 10;

const ImportFromDocumentModal = ({
  isOpen,
  onClose,
  onImport,
  onError,
  fabricId,
  templateName,
  libraryTemplateId,
  libraryTemplateName,
  onProcessingStart,
  onProcessingEnd,
  onFullScreenChange,
}) => {

  // Thread list state
  const [threads, setThreads] = useState([]);
  const [threadsPage, setThreadsPage] = useState(1);
  const [threadsCursorPage, setThreadsCursorPage] = useState(0);
  const [threadsApiTotal, setThreadsApiTotal] = useState(0);
  const [threadsApiFetched, setThreadsApiFetched] = useState(0);
  const [allFiltered, setAllFiltered] = useState([]);
  const [threadSearchInput, setThreadSearchInput] = useState("");
  const [threadSearch, setThreadSearch] = useState("");
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [isPageChanging, setIsPageChanging] = useState(false);

  // Doc picker state
  const [docsPage, setDocsPage] = useState(1);
  const [isDocsPageChanging, setIsDocsPageChanging] = useState(false);
  const [expandedThreadId, setExpandedThreadId] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [selectedDocName, setSelectedDocName] = useState("");
  const [docSearch, setDocSearch] = useState("");

  const [isMapping, setIsMapping] = useState(false);
  const [mappingData, setMappingData] = useState(null);
  const [compatWarning, setCompatWarning] = useState(null);

  // Generation / progress state
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [liveMessage, setLiveMessage] = useState("");
  const [displayPct, setDisplayPct] = useState(0);
  const displayPctRef = useRef(0);
  const targetPctRef = useRef(0);
  const animFrameRef = useRef(null);
  const pollAbortRef = useRef(false);
  const lastMessageRef = useRef("");

  const handleClose = () => {
    setExpandedThreadId(null);
    setSelectedDocId(null);
    setSelectedDocName("");
    setDocSearch("");
    setDocsPage(1);
    onClose();
  };

  const listRef = useRef(null);
  useOutsideClick(listRef, handleClose, isGenerating || isMapping || !!mappingData || !!compatWarning);

  const onImportRef = useRef(onImport);
  const onErrorRef = useRef(onError);
  const onProcessingStartRef = useRef(onProcessingStart);
  const onProcessingEndRef = useRef(onProcessingEnd);
  useEffect(() => { onImportRef.current = onImport; }, [onImport]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { onProcessingStartRef.current = onProcessingStart; }, [onProcessingStart]);
  useEffect(() => { onProcessingEndRef.current = onProcessingEnd; }, [onProcessingEnd]);

  const onFullScreenChangeRef = useRef(onFullScreenChange);
  useEffect(() => { onFullScreenChangeRef.current = onFullScreenChange; }, [onFullScreenChange]);

  const isFullScreenPhase = !!mappingData || isGenerating;
  useEffect(() => {
    if (onFullScreenChangeRef.current) onFullScreenChangeRef.current(isFullScreenPhase);
    return () => {
      if (isFullScreenPhase && onFullScreenChangeRef.current) {
        onFullScreenChangeRef.current(false);
      }
    };
  }, [isFullScreenPhase]);

  const MESSAGE_PROGRESS_MAP = [
    { prefix: "processing...",                      pct: 5,  label: "Starting import..."    },
    { prefix: "loading heading-mapped template",    pct: 8,  label: "Loading template..."   },
    { prefix: "fetching document",                  pct: 15, label: "Reading document..."   },
    { prefix: "detecting document language",        pct: 22, label: "Extracting content..." },
    { prefix: "extracting matched section content", pct: 30, label: "Extracting content..." },
    { prefix: "matched",                            pct: 38, label: "Preparing sections..." },
    { prefix: "building preview",                   pct: 92, label: "Building preview..."   },
    { prefix: "writing results",                    pct: 96, label: "Writing results..."    },
  ];

  const resolveProgress = (msg) => {
    const fallback = { pct: targetPctRef.current, label: msg };
    if (!msg) return fallback;
    const lower = msg.toLowerCase();

    // "Processing sections: X/N done..." → map into the 40–88% band
    const procM = lower.match(/processing sections:\s*(\d+)\/(\d+)/);
    if (procM) {
      const x = parseInt(procM[1], 10);
      const n = parseInt(procM[2], 10);
      if (n > 0) {
        return {
          pct: Math.round(40 + (x / n) * 48),
          label: `Processing sections: ${x}/${n}`,
        };
      }
    }

    for (const { prefix, pct, label } of MESSAGE_PROGRESS_MAP) {
      if (lower.startsWith(prefix)) {
        return { pct: pct ?? targetPctRef.current, label };
      }
    }
    return fallback;
  };

  const startEaseTo = (targetPct) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    targetPctRef.current = targetPct;
    const tick = () => {
      const current = displayPctRef.current;
      const target = targetPctRef.current;
      if (current >= target) {
        displayPctRef.current = target;
        setDisplayPct(Math.round(target));
        animFrameRef.current = null;
        return;
      }
      const delta = Math.max((target - current) * 0.06, 0.3);
      const next = Math.min(current + delta, target);
      displayPctRef.current = next;
      setDisplayPct(Math.round(next));
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  };

  useEffect(() => {
    if (!isGenerating && animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, [isGenerating]);

  const API_BASE_URL = "/pceapi/genbi";
  const THREADS_TREE_URL = "/pceapi/fabrics/threads-tree";
  const headers = { Authorization: GetCookie("access_token") };

  // Threads helpers
  const filterThreads = (rawThreads) =>
    (rawThreads || [])
      .filter((t) => (t.document_count ?? (t.documents || []).length) > 0)
      .filter((t) => countValidDocs(t) > 0);

  const fetchThreadsTreePage = async (page) => {
    const response = await axios.post(
      THREADS_TREE_URL,
      {
        page,
        size: THREADS_PAGE_SIZE,
        fabric_id: fabricId,
        ...(threadSearch ? { search: threadSearch } : {}),
      },
      { headers }
    );
    if (response.data?.status === "success") {
      return {
        rawThreads: response.data.data || [],
        total: response.data.total || 0,
      };
    }
    throw new Error(response.data?.message || "Failed to fetch data collections.");
  };

  // Load threads when modal opens / search changes
  useEffect(() => {
    if (!isOpen || !fabricId) return;
    let cancelled = false;

    setExpandedThreadId(null);
    setSelectedDocId(null);
    setSelectedDocName("");
    setDocSearch("");
    setDocsPage(1);
    setThreadsPage(1);
    setThreadsCursorPage(0);
    setThreadsApiTotal(0);
    setThreadsApiFetched(0);
    setAllFiltered([]);
    setThreads([]);

    const loadInitial = async () => {
      setIsLoadingThreads(true);
      try {
        let buffer = [];
        let consumed = 0;
        let apiTotal = 0;
        let lastPage = 0;

        for (let i = 0; i < BACKFILL_MAX_PAGES; i++) {
          if (cancelled) return;
          const nextPage = lastPage + 1;
          const { rawThreads, total } = await fetchThreadsTreePage(nextPage);
          apiTotal = total;
          consumed += rawThreads.length;
          lastPage = nextPage;
          buffer = [...buffer, ...filterThreads(rawThreads)];
          if (buffer.length >= THREADS_PAGE_SIZE || consumed >= apiTotal) break;
        }

        if (cancelled) return;
        setAllFiltered(buffer);
        setThreads(buffer.slice(0, THREADS_PAGE_SIZE));
        setThreadsCursorPage(lastPage);
        setThreadsApiFetched(consumed);
        setThreadsApiTotal(apiTotal);
      } catch (err) {
        if (cancelled) return;
        onError(err?.message || "An error occurred while fetching data collections.");
        console.error("Error fetching threads-tree:", err);
      } finally {
        if (!cancelled) setIsLoadingThreads(false);
      }
    };

    loadInitial();
    return () => { cancelled = true; };
  }, [isOpen, threadSearch, fabricId]);

  const ensureFilteredFor = async (targetPage) => {
    const required = targetPage * THREADS_PAGE_SIZE;
    let buffer = allFiltered;
    let consumed = threadsApiFetched;
    let lastPage = threadsCursorPage;
    let apiTotal = threadsApiTotal;

    if (buffer.length >= required || consumed >= apiTotal) return buffer;

    setIsBackfilling(true);
    try {
      for (let i = 0; i < BACKFILL_MAX_PAGES; i++) {
        const nextPage = lastPage + 1;
        const { rawThreads, total } = await fetchThreadsTreePage(nextPage);
        apiTotal = total;
        consumed += rawThreads.length;
        lastPage = nextPage;
        buffer = [...buffer, ...filterThreads(rawThreads)];
        if (buffer.length >= required || consumed >= apiTotal) break;
      }
      setAllFiltered(buffer);
      setThreadsCursorPage(lastPage);
      setThreadsApiFetched(consumed);
      setThreadsApiTotal(apiTotal);
      return buffer;
    } catch (err) {
      onError(err?.message || "An error occurred while loading more data collections.");
      return buffer;
    } finally {
      setIsBackfilling(false);
    }
  };

  const handleThreadsPrev = async () => {
    if (threadsPage <= 1) return;
    setIsPageChanging(true);
    const minDelay = new Promise((r) => setTimeout(r, 300));
    const prev = threadsPage - 1;
    const start = (prev - 1) * THREADS_PAGE_SIZE;
    await minDelay;
    setThreadsPage(prev);
    setThreads(allFiltered.slice(start, start + THREADS_PAGE_SIZE));
    setIsPageChanging(false);
  };

  const handleThreadsNext = async () => {
    setIsPageChanging(true);
    const minDelay = new Promise((r) => setTimeout(r, 300));
    const next = threadsPage + 1;
    const buffer = await ensureFilteredFor(next);
    const start = (next - 1) * THREADS_PAGE_SIZE;
    const slice = buffer.slice(start, start + THREADS_PAGE_SIZE);
    await minDelay;
    if (slice.length === 0) { setIsPageChanging(false); return; }
    setThreadsPage(next);
    setThreads(slice);
    setIsPageChanging(false);
  };

  const handleExpandThread = (threadId) => {
    setExpandedThreadId(threadId);
    setSelectedDocId(null);
    setSelectedDocName("");
    setDocSearch("");
    setDocsPage(1);
  };

  const handleSelectDoc = (doc, isDocSelected) => {
    setSelectedDocId(isDocSelected ? null : doc.id);
    setSelectedDocName(isDocSelected ? "" : doc.document_name || doc.name || "");
  };

  // ── Phase 1: build the heading mapping ────────────────────────────────────
  // Shared helper — calls heading-mapping API with optional force flag
  const _callHeadingMapping = async (force = false) => {
    const resp = await axios.post(
      `${API_BASE_URL}/doc-import/heading-mapping`,
      {
        doc_id: selectedDocId,
        fabric_id: fabricId,
        library_template_id: libraryTemplateId,
        template_name: templateName || null,
        ...(force ? { force: true } : {}),
      },
      { headers }
    );
    return resp;
  };

  // Phase 1 (initial click): calls without force — may get needs_confirmation
  const handleGenerate = async () => {
    if (!selectedDocId || !fabricId || !templateName?.trim()) return;
    if (!libraryTemplateId) return;

    const _onError           = onErrorRef.current;
    const _onProcessingStart = onProcessingStartRef.current;
    const _onProcessingEnd   = onProcessingEndRef.current;

    setIsMapping(true);
    setMappingData(null);
    setCompatWarning(null);
    if (_onProcessingStart) _onProcessingStart("Analysing document & building mapping...");

    try {
      const mappingResp = await _callHeadingMapping(false);

      if (mappingResp.data?.status === "needs_confirmation") {
        setCompatWarning(mappingResp.data);
      } else if (mappingResp.data?.status === "success") {
        setMappingData(mappingResp.data);
      } else {
        _onError(mappingResp.data?.message || "Failed to match document headings.");
      }
    } catch (error) {
      _onError(
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while matching document headings."
      );
      console.error("Error creating heading mapping:", error);
    } finally {
      setIsMapping(false);
      if (_onProcessingEnd) _onProcessingEnd();
    }
  };

  // Called from warning screen "Proceed anyway" — calls with force=true
  const handleProceedAnyway = async () => {
    const _onError           = onErrorRef.current;
    const _onProcessingStart = onProcessingStartRef.current;
    const _onProcessingEnd   = onProcessingEndRef.current;

    setCompatWarning(null);
    setIsMapping(true);
    if (_onProcessingStart) _onProcessingStart("Analysing document & building mapping...");

    try {
      const mappingResp = await _callHeadingMapping(true);

      if (mappingResp.data?.status === "success") {
        setMappingData(mappingResp.data);
      } else {
        _onError(mappingResp.data?.message || "Failed to match document headings.");
      }
    } catch (error) {
      _onError(
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while matching document headings."
      );
      console.error("Error creating heading mapping (force):", error);
    } finally {
      setIsMapping(false);
      if (_onProcessingEnd) _onProcessingEnd();
    }
  };

  const handleMappingCancel = () => {
    setMappingData(null);
  };

  // ── Phase 2: save the reviewed mapping, then run the import pipeline ──────
  const handleMappingConfirm = async (amendedTemplate) => {
    const headingMappingTemplateId = mappingData?.template_id ?? null;
    setMappingData(null);

    const _onImport        = onImportRef.current;
    const _onError         = onErrorRef.current;
    const _onProcessingEnd = onProcessingEndRef.current;

    pollAbortRef.current = false;
    setIsGenerating(true);
    setCurrentStepIndex(0);
    setLiveMessage("");
    lastMessageRef.current = "";
    displayPctRef.current = 0;
    targetPctRef.current = 0;
    setDisplayPct(0);
    setLiveMessage("Saving mapping...");
    startEaseTo(3);

    const POLL_INTERVAL_MS = 10000;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    let previewData = null;
    let finalError = null;

    try {
      // ── Step 1: Persist the reviewed mapping ─────────────────────────────
      const saveResp = await axios.post(
        `${API_BASE_URL}/doc-import/heading-mapping/save`,
        {
          fabric_id: fabricId,
          template_id: headingMappingTemplateId,
          amended_template: amendedTemplate,
        },
        { headers }
      );

      if (saveResp.data?.status !== "success") {
        finalError = saveResp.data?.message || "Failed to save the reviewed mapping.";
        return;
      }

      // ── Step 2: Enqueue mapped import job ────────────────────────────────
      const triggerResp = await axios.post(
        `${API_BASE_URL}/import-old-dossier`,
        {
          doc_id: selectedDocId,
          fabric_id: fabricId,
          template_name: templateName || null,
          library_template_id: libraryTemplateId,   // mapped pipeline
          heading_mapping_template_id: headingMappingTemplateId,
        },
        { headers }
      );

      if (triggerResp.data?.status !== "success") {
        finalError = triggerResp.data?.message || "Failed to start template generation.";
        return;
      }

      const templateId = triggerResp.data.template_id;
      const targetFabricId = triggerResp.data.fabric_id || fabricId;

      setLiveMessage("Starting import...");
      startEaseTo(5);

      // ── Step 3: Poll until terminal ──────────────────────────────────────
      let firstPoll = true;
      while (!pollAbortRef.current) {
        if (!firstPoll) await sleep(POLL_INTERVAL_MS);
        firstPoll = false;
        if (pollAbortRef.current) break;

        let pollResp;
        try {
          pollResp = await axios.post(
            `${API_BASE_URL}/import-doc/status`,
            { fabric_id: targetFabricId, template_id: templateId },
            { headers }
          );
        } catch (e) {
          console.warn("[DocImport] poll request error:", e);
          continue;
        }

        const status = pollResp.data?.status;

        if (pollResp.data?.message && typeof pollResp.data.message === "string") {
          const newMsg = pollResp.data.message;
          if (newMsg !== lastMessageRef.current) {
            lastMessageRef.current = newMsg;
            const { pct, label } = resolveProgress(newMsg);
            if (label !== null) setLiveMessage(label);
            if (pct > targetPctRef.current) startEaseTo(pct);
          }
        }

        if (status === "processing") continue;
        if (status === "completed") {
          previewData = pollResp.data.preview || pollResp.data.data || null;
          break;
        }
        if (status === "failed") {
          finalError = pollResp.data?.message || "Template generation failed.";
          break;
        }
        finalError = pollResp.data?.message || "Unexpected response from server.";
        break;
      }

      if (pollAbortRef.current) return;

      if (previewData) {
        _onImport({
          name:          previewData.suggested_template_name || previewData.name || "",
          sections:      previewData.sections || [],
          format_json:   previewData.format_json || null,
          frontpage:     previewData.frontpage || null,
          template_id:   previewData.template_id || null,
          fabric_id:     previewData.source?.fabric_id || previewData.fabric_id || targetFabricId,
          source_doc_id: previewData.source?.doc_id || previewData.doc_id || selectedDocId,
          library_template_id: libraryTemplateId,
          heading_mapping_id: headingMappingTemplateId,
          doc_generated: true,
          source_lang_id:   previewData.language?.source_lang_id ?? null,
          source_lang_name: previewData.language?.source_lang_name ?? null,
        });
      } else if (!finalError) {
        finalError = "No preview data received from generation.";
      }
    } catch (error) {
      finalError =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while generating the template.";
      console.error("Error generating template:", error);
    } finally {
      if (!finalError) {
        setCurrentStepIndex(6);
        startEaseTo(100);
        await sleep(800);
      }
      setIsGenerating(false);
      setCurrentStepIndex(0);
      setLiveMessage("");
      if (finalError) _onError(finalError);
      handleClose();
      if (_onProcessingEnd) _onProcessingEnd();
    }
  };

  if (!isOpen && !isGenerating && !isMapping && !mappingData && !compatWarning) return null;

  // ── Low document/template compatibility warning ───────────────────────────
  const getCompatWarningBodyContent = () => {
    return (
      <div>
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Warning:</strong> The document headings have low similarity with the template.
        </div>
        <p>
          The selected document <strong>{selectedDocName}</strong> does not appear to match
          the template <strong>{libraryTemplateName}</strong>.
        </p>
        <p className="text-muted mb-0">
          You can still proceed with heading mapping, but the suggested sections and agents may not be meaningful for this document.
        </p>
      </div>
    );
  };

  // ── Heading mapping review (phase 1 result) ───────────────────────────────
  if (mappingData) {
    return (
      <GenbiHeadingMappingModal
        isOpen
        mappingData={mappingData}
        docName={selectedDocName}
        libraryTemplateName={libraryTemplateName}
        onCancel={handleMappingCancel}
        onConfirm={handleMappingConfirm}
      />
    );
  }

  // ── Generating overlay (phase 2) ──────────────────────────────────────────
  if (isGenerating) {
    const stepIconMap = {
      0: "bi-hourglass-split",
      1: "bi-cloud-arrow-down-fill",
      2: "bi-file-earmark-text",
      3: "bi-layers",
      6: "bi-check-circle-fill",
    };
    const currentIcon = stepIconMap[currentStepIndex] || stepIconMap[0];

    return createPortal(
      <div
        className="ev-modal-overlay d-flex align-items-center justify-content-center"
        style={{ zIndex: "var(--num-1000000000, 1000000000)" }}
      >
        <div
          className="ev-card-4"
          style={{ width: "360px", maxWidth: "95vw", padding: "20px 24px" }}
        >
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
              <i
                className={`bi ${currentIcon} ev-reg-p-blue`}
                style={{ fontSize: "18px" }}
              />
              <div style={{ minWidth: 0 }}>
                <p className="ev-reg-p-blue ev-bold-med mb-0">Generating Template</p>
                {libraryTemplateName && (
                  <p
                    className="ev-xsmall-p-dark mb-0 text-truncate"
                    style={{ opacity: 0.45 }}
                    title={libraryTemplateName}
                  >
                    {libraryTemplateName}
                  </p>
                )}
                <p className="ev-xsmall-p-dark mb-0" style={{ opacity: 0.55 }}>
                  {liveMessage}
                </p>
              </div>
            </div>
            <span className="ev-bold-med ev-reg-p-blue">{displayPct}%</span>
          </div>
          <div className="genbi-progress-track">
            <div
              className="genbi-progress-fill"
              style={{ width: `${displayPct}%` }}
            />
            <div
              className="genbi-progress-shimmer"
              style={{ width: `${displayPct}%` }}
            />
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ── No project selected — show a warning instead of the picker ──────────────
  if (!fabricId) {
    return (
      <div
        ref={listRef}
        className="ev-message-card2 d-block ev-card-3 d-flex flex-column overflow-hidden position-absolute z-1000 mt-1 p-2"
        style={{ width: "26rem", maxWidth: "90vw" }}
      >
        <div className="ev-alert-card-warning py-2 px-3">
          <p className="mb-0 ev-xsmall-p-dark">
            <i className="bi bi-info-circle me-1"></i>
            Please select a project first.
          </p>
        </div>
      </div>
    );
  }

  // ── Document picker ─────────────────────────────────────────────────────────
  const renderPicker = () => {
    if (expandedThreadId) {
      const expandedThread =
        allFiltered.find((c) => c.id === expandedThreadId) ||
        threads.find((c) => c.id === expandedThreadId);
      if (!expandedThread) return null;

      const validDocs = (expandedThread.documents || []).filter(isAllowedDocType);
      const searchedDocs = docSearch.trim()
        ? validDocs.filter((d) =>
            (d.document_name || d.name || "")
              .toLowerCase()
              .includes(docSearch.toLowerCase())
          )
        : validDocs;

      const docsTotal = searchedDocs.length;
      const docsTotalPages = Math.max(1, Math.ceil(docsTotal / DOCS_PAGE_SIZE));
      const safeDocsPage = Math.min(docsPage, docsTotalPages);
      const docsStart = (safeDocsPage - 1) * DOCS_PAGE_SIZE;
      const visibleDocs = searchedDocs.slice(docsStart, docsStart + DOCS_PAGE_SIZE);

      return (
        <div
          className="d-flex flex-column flex-grow-1 overflow-hidden ev-padding-small py-1"
          style={{ minHeight: 0 }}
        >
          {/* Header row */}
          <div className="d-flex align-items-center justify-content-between mb-1 flex-shrink-0">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-small py-0"
              onClick={() => {
                setExpandedThreadId(null);
                setSelectedDocId(null);
                setSelectedDocName("");
                setDocSearch("");
                setDocsPage(1);
              }}
            >
              <i className="bi bi-arrow-left"></i>
            </button>

            <div className="flex-grow-1 text-center px-2">
              <p className="mb-0 ev-reg-p-blue ev-bold-reg text-truncate">
                {expandedThread.name || expandedThread.thread_name}
              </p>
            </div>
          </div>

          {/* Document search */}
          <div className="position-relative mb-2 flex-shrink-0">
            <input
              type="text"
              className="form-control search-input ms-0"
              placeholder="Search documents..."
              value={docSearch}
              onChange={(e) => {
                setDocSearch(e.target.value);
                setDocsPage(1);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  setDocsPage(1);
                }
              }}
            />
            <button
              type="button"
              className="ev-btn-lightblue-fill-round search-bar-button"
            >
              <i className="bi bi-search"></i>
            </button>
          </div>

          {/* Document list */}
          <div
            className="scrollable-element flex-grow-1 pe-2"
            style={{ minHeight: 0 }}
          >
            {isDocsPageChanging ? (
              <div className="text-center py-4">
                <div
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : visibleDocs.length === 0 ? (
              <div className="ev-alert-card-warning py-2 px-3">
                <p className="mb-0 ev-xsmall-p-dark">
                  <i className="bi bi-info-circle me-1"></i>
                  {docSearch.trim()
                    ? "No documents match your search."
                    : "No compatible documents in this data collection."}
                </p>
              </div>
            ) : (
              visibleDocs.map((doc, docIndex) => {
                const isDocSelected = selectedDocId === doc.id;
                const badgeType = (
                  doc.file_type ||
                  doc.document_type ||
                  ""
                ).toString();
                const tooltipClass = docIndex === 0 ? "avatar-tooltip-bottom" : "avatar-tooltip";
                return (
                  <div
                    key={doc.id}
                    className={`ev-card-7 d-flex align-items-center justify-content-between mb-1 cursor-pointer p-1 px-2 ${tooltipClass} ${
                      isDocSelected ? "active" : ""
                    }`}
                    data-tooltip={doc.document_name || doc.name || ""}
                    onClick={() => handleSelectDoc(doc, isDocSelected)}
                  >
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                      <input
                        className="form-check-input m-1 me-2 flex-shrink-0"
                        type="radio"
                        checked={isDocSelected}
                        onChange={() => handleSelectDoc(doc, false)}
                      />
                      <div className="text-truncate">
                        <span className="ev-xsmall-p-blue">
                          <i className="bi bi-file-earmark me-1"></i>
                          {(doc.document_name || doc.name || "").length > 50
                            ? `${(doc.document_name || doc.name).slice(0, 50)}...`
                            : doc.document_name || doc.name}
                        </span>
                      </div>
                    </div>
                    {badgeType && (
                      <span className="ev-message-card6 ev-btn-xsmall border-0 ev-xsmall-p-blue ms-2">
                        {badgeType.toUpperCase()}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="d-flex align-items-center flex-wrap gap-2 mt-2 flex-shrink-0">
            <div className="flex-grow-1"></div>

            <span
              className="align-top w-14 me-2 avatar-tooltip ev-btn-xsmall"
              data-tooltip={
                !templateName?.trim()
                  ? "Please enter a template name"
                  : !fabricId
                  ? "Please select a project first"
                  : !selectedDocId
                  ? "Please select a document"
                  : !libraryTemplateId
                  ? "Please import a library template first"
                  : undefined
              }
            >
              <button
                type="button"
                className="ev-btn-blue-fill-round ev-bold-reg"
                disabled={
                  !selectedDocId ||
                  !fabricId ||
                  !libraryTemplateId ||
                  isGenerating ||
                  isMapping ||
                  !templateName?.trim()
                }
                onClick={handleGenerate}
                style={
                  !selectedDocId ||
                  !fabricId ||
                  !libraryTemplateId ||
                  !templateName?.trim()
                    ? { pointerEvents: "none" }
                    : {}
                }
              >
                {isGenerating || isMapping ? (
                  <>

                    {isMapping ? "Mapping..." : "Generating..."}
                  </>
                ) : (
                  <>
                    <i className="bi bi-magic me-1"></i>Generate
                  </>
                )}
              </button>
            </span>
          </div>
        </div>
      );
    }

    // Thread list (collapsed view)
    const apiExhausted =
      threadsApiFetched >= threadsApiTotal && threadsApiTotal > 0;
    const hasNextPage =
      threadsPage * THREADS_PAGE_SIZE < allFiltered.length || !apiExhausted;
    const hasPrevPage = threadsPage > 1;

    return (
      <div
        className="d-flex flex-column flex-grow-1 overflow-hidden ev-padding-small py-1"
        style={{ minHeight: 0 }}
      >
        {/* Thread search */}
        <div className="position-relative mb-2 flex-shrink-0">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search data collections..."
            value={threadSearchInput}
            onChange={(e) => setThreadSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setThreadSearch(threadSearchInput.trim());
                setThreadsPage(1);
              }
            }}
          />
          <button
            type="button"
            className="ev-btn-lightblue-fill-round search-bar-button"
            onClick={() => {
              setThreadSearch(threadSearchInput.trim());
              setThreadsPage(1);
            }}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>

        {/* Thread list */}
        <div
          className="scrollable-element flex-grow-1 pe-2"
          style={{ minHeight: 0 }}
        >
          {isLoadingThreads || isPageChanging ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : threads.length === 0 ? (
            <div className="ev-alert-card-warning text-center py-3">
              <p className="mb-0">
                <i className="bi bi-info-circle me-1"></i>
                {threadSearch
                  ? "No data collections match your search."
                  : "No data collections available."}
              </p>
            </div>
          ) : (
            threads.map((thread) => {
              const docCount = countValidDocs(thread);
              return (
                <div
                  key={thread.id}
                  className="ev-card-2 cursor-pointer mb-1 py-1"
                  onClick={() => handleExpandThread(thread.id)}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1 pe-2">
                      <p className="mb-0 ev-reg-p-blue ev-bold-reg text-truncate">
                        {(thread.name || "").length > 40
                          ? `${thread.name.slice(0, 40)}...`
                          : thread.name}
                      </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="ev-message-card6 ev-btn-xsmall border-0 ev-xsmall-p-blue">
                        <i className="bi bi-file-earmark-text me-1"></i>
                        {docCount} docs
                      </span>
                      <i className="bi bi-chevron-right ev-xsmall-p-blue"></i>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {!isLoadingThreads && (threads.length > 0 || hasPrevPage) && (
          <div className="d-flex align-items-center justify-content-between mt-1 flex-shrink-0">
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
              disabled={!hasPrevPage || isBackfilling || isPageChanging}
              onClick={handleThreadsPrev}
            >
              <i className="bi bi-chevron-left me-1"></i>Prev
            </button>
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-small ev-bold-reg"
              disabled={!hasNextPage || isBackfilling || isPageChanging}
              onClick={handleThreadsNext}
            >
              Next<i className="bi bi-chevron-right ms-1"></i>
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <>
    <div
      ref={listRef}
      className="ev-message-card2 d-block ev-card-3 d-flex flex-column overflow-hidden position-absolute z-1000 mt-1 p-0"
      style={{ width: "26rem", maxWidth: "90vw", maxHeight: "33vh" }}
    >
      {/* Imported library template context */}
      {libraryTemplateName && (
        <div className="d-flex align-items-center gap-1 ev-message-card3 ev-btn-xsmall px-2 mt-2 mx-2 align-self-start flex-shrink-0">
          <i className="bi bi-collection ev-xsmall-p-blue"></i>
          <span
            className="ev-xsmall-p-blue text-truncate"
            style={{ maxWidth: "200px" }}
          >
            {libraryTemplateName}
          </span>
        </div>
      )}

      {/* Picker */}
      <div
        className="d-flex flex-column flex-grow-1 overflow-hidden"
        style={{ minHeight: 0 }}
      >
        {renderPicker()}
      </div>
    </div>

    <ConfirmationModal
      show={!!compatWarning}
      onClose={() => setCompatWarning(null)}
      onConfirm={handleProceedAnyway}
      title="Low Document Compatibility"
      message=""
      confirmButtonText="Proceed anyway"
      confirmButtonClass="btn-warning"
      cancelButtonText="Close"
      bodyContent={getCompatWarningBodyContent()}
    />
    </>
  );
};

export default ImportFromDocumentModal;